import { CountrySelector } from '@components/CountrySelector';
import { SelectInput } from '@gurupay/gp-ui-library';
import { memo, useMemo } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { FXFormFieldConfig } from 'types/operations';

import { useValidationRules } from '../hooks/useValidationRules.ts';

interface BaseFormFieldProps {
	fieldKey: string;
	fieldConfig: FXFormFieldConfig;
	disabled?: boolean;
}

export const SelectFormField = memo(
	({ fieldKey, fieldConfig, disabled }: BaseFormFieldProps) => {
		const { t } = useTranslation();
		const { control } = useFormContext();
		const rules = useValidationRules(fieldKey, fieldConfig);
		const { field, fieldState } = useController({ name: fieldKey, control, rules });

		const options = useMemo(
			() =>
				(fieldConfig.options ?? []).map((o) => {
					if (typeof o === 'string') {
						return { label: o, value: o };
					}
					return { label: o.name ?? o.label ?? '', value: o.code ?? o.value ?? '' };
				}),
			[fieldConfig.options],
		);

		const isCountryField = fieldKey.endsWith('country');
		const isFieldDisabled = disabled || fieldConfig.disabled;

		return (
			<div>
				{isCountryField ? (
					<CountrySelector
						{...field}
						className="max-w-125"
						dataTestId={fieldKey}
						error={fieldState.error?.message}
						isDisabled={isFieldDisabled}
						isRequired={fieldConfig.required}
						label={t(`payments.newPayment.additionalData.fx.form.${fieldConfig.label}`)}
						onChange={(val?: string) => field.onChange(val ?? null)}
						options={options}
						placeholder={fieldConfig.placeholder}
					/>
				) : (
					<SelectInput
						{...field}
						className="max-w-125"
						dataTestId={fieldKey}
						error={fieldState.error?.message}
						isDisabled={isFieldDisabled}
						isRequired={fieldConfig.required}
						label={t(`payments.newPayment.additionalData.fx.form.${fieldConfig.label}`)}
						onChange={(val?: string) => field.onChange(val ?? null)}
						options={options}
						placeholder={fieldConfig.placeholder}
						showClear
					/>
				)}
			</div>
		);
	},
);

SelectFormField.displayName = 'SelectFormField';
